/* v8 ignore file */
/* v8 ignore next 500 */
import { ref, computed, readonly } from 'vue'
import { Capacitor } from '@capacitor/core'
import { consola } from 'consola'
import { useGetWidth } from './useGetWidth'

const logger = consola.withTag('safe-area')

/**
 * Интерфейс для безопасных зон (safe area insets)
 */
export interface SafeAreaInsets {
  top: number
  bottom: number
  left: number
  right: number
}

// Глобальное состояние (Singleton)
const safeAreaInsets = ref<SafeAreaInsets>({
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
})

const isInitialized = ref(false)
const platform = ref<'ios' | 'android' | 'web'>('web')

/**
 * Composable для работы с безопасными зонами (safe area)
 *
 * ✅ Работает на iOS (notch, Dynamic Island)
 * ✅ Работает на Android (status bar, navigation bar)
 * ✅ Автоматическое обнаружение при изменении ориентации
 * ✅ Следует принципам SOLID и DRY
 *
 * @example
 * const { safeTop, safeBottom, headerPadding } = useSafeArea()
 *
 * // В template:
 * <header :style="{ paddingTop: headerPadding }">
 */
export const useSafeArea = () => {
  // Получаем реактивный width внутри composable для правильной работы lifecycle hooks
  const { width } = useGetWidth()

  /**
   * Инициализирует систему safe area
   */
  const initialize = (): void => {
    if (isInitialized.value) {
      logger.debug('✅ Safe area уже инициализирована');
      return;
    }

    if (!process.client) {
      logger.debug('⚠️ Не клиентская среда, пропускаем инициализацию');
      return;
    }

    // 🧪 ТЕСТИРОВАНИЕ: Отключаем safe area для тестов (глобальный флаг)
    const DISABLE_SAFE_AREA_FOR_TESTING = false; // ✅ Измените на false чтобы включить обратно

    if (DISABLE_SAFE_AREA_FOR_TESTING) {
      logger.warn('🧪 Safe area ОТКЛЮЧЕНА для тестирования - все insets = 0');
      setSafeAreaInsets({ top: 0, bottom: 0, left: 0, right: 0 });
      isInitialized.value = true;
      return;
    }

    try {
      const platformName = Capacitor.getPlatform();
      platform.value = platformName as 'ios' | 'android' | 'web';

      logger.info(`🚀 Инициализация safe area для платформы: ${platformName}`);

      if (Capacitor.isNativePlatform()) initializeNativeSafeArea();
      else initializeWebSafeArea();

      isInitialized.value = true;
      logger.success('✅ Safe area инициализирована', safeAreaInsets.value);
    } catch (error) {
      logger.error('❌ Ошибка инициализации safe area:', error);
      // Fallback к значениям по умолчанию
      setSafeAreaInsets({ top: 0, bottom: 0, left: 0, right: 0 });
      isInitialized.value = true;
    }
  }

  /**
   * Инициализирует safe area для нативных платформ (iOS/Android).
   *
   * Плагин @capacitor-community/safe-area v7 не отдаёт insets через JS-API
   * (методов enable()/getSafeAreaInsets() больше нет): на новых WebView он
   * прокидывает их в настоящие env(safe-area-inset-*), на старых — отступает
   * вебвью нативно, и тогда честный inset для веб-слоя равен нулю. Старые
   * нативные шеллы (v5) инжектят px-значения в var(--safe-area-inset-*).
   * Единственный источник, покрывающий все три режима, — CSS-замер max(env, var).
   */
  const initializeNativeSafeArea = (): void => {
    try {
      const insets = getCSSEnvironmentInsets()
      logger.info('✅ Safe area получена через CSS (env/var)', insets)
      // Нули не подменяем дефолтами: когда вебвью отступлен нативно, системные
      // панели лежат вне него и нулевой inset — правильный ответ. Выдуманные
      // значения (раньше Android получал top:28/bottom:0) ломали реальные.
      setSafeAreaInsets(insets)
    } catch (error) {
      logger.warn('⚠️ Ошибка замера safe area, оставляем нули', error)
      setSafeAreaInsets({ top: 0, bottom: 0, left: 0, right: 0 })
    }

    // Плагин запускается на этапе 'pre', а нативный шелл доносит insets позже
    // (env() наполняется первым диспатчем, старые шеллы инжектят var() после
    // загрузки страницы) — поэтому несколько отложенных перезамеров
    for (const delay of [300, 1000, 3000]) {
      setTimeout(() => { updateSafeArea() }, delay)
    }

    // Подписываемся на изменения ориентации
    subscribeToOrientationChanges()
  }

  /**
   * Инициализирует safe area для веб-версии
   */
  const initializeWebSafeArea = (): void => {
    logger.debug('🌐 Web платформа, используем CSS env() или defaults')

    const insets = getCSSEnvironmentInsets()

    if (insets.top === 0 && insets.bottom === 0) {
      // На web обычно не нужны safe area insets
      setSafeAreaInsets({ top: 0, bottom: 0, left: 0, right: 0 })
    } else {
      setSafeAreaInsets(insets)
    }
  }

  /**
   * Замеряет эффективные insets через CSS: максимум из env(safe-area-inset-*)
   * (iOS, новые Android WebView) и var(--safe-area-inset-*) (значения, которые
   * инжектят старые нативные шеллы). В Android WebView env() часто равен нулю —
   * поэтому один env() в качестве источника недостаточен.
   */
  const getCSSEnvironmentInsets = (): SafeAreaInsets => {
    if (!process.client) {
      return { top: 0, bottom: 0, left: 0, right: 0 }
    }

    // Создаем временный элемент для получения CSS переменных
    const testElement = document.createElement('div')
    testElement.style.position = 'fixed'
    testElement.style.top = '0'
    testElement.style.left = '0'
    testElement.style.width = '1px'
    testElement.style.height = '1px'
    testElement.style.visibility = 'hidden'
    testElement.style.pointerEvents = 'none'

    testElement.style.paddingTop = 'max(env(safe-area-inset-top, 0px), var(--safe-area-inset-top, 0px))'
    testElement.style.paddingBottom = 'max(env(safe-area-inset-bottom, 0px), var(--safe-area-inset-bottom, 0px))'
    testElement.style.paddingLeft = 'max(env(safe-area-inset-left, 0px), var(--safe-area-inset-left, 0px))'
    testElement.style.paddingRight = 'max(env(safe-area-inset-right, 0px), var(--safe-area-inset-right, 0px))'

    document.body.append(testElement)

    const computed = window.getComputedStyle(testElement)
    const insets: SafeAreaInsets = {
      top: Number.parseFloat(computed.paddingTop) || 0,
      bottom: Number.parseFloat(computed.paddingBottom) || 0,
      left: Number.parseFloat(computed.paddingLeft) || 0,
      right: Number.parseFloat(computed.paddingRight) || 0,
    }

    testElement.remove()

    return insets
  }

  /**
   * Устанавливает значения safe area insets (только реактивное JS-состояние).
   *
   * ВАЖНО: --safe-area-inset-* в <html> НЕ записываем. Inline-значения
   * перекрывали бы живые env() из :root (assets/css/global.scss) и значения,
   * инжектированные нативным шеллом, — именно так на Android появлялся
   * жёсткий bottom:0px для всего приложения, и инпут чата поддержки уезжал
   * под системную навигацию (см. pages/support.vue).
   */
  const setSafeAreaInsets = (insets: SafeAreaInsets): void => {
    safeAreaInsets.value = { ...insets }
    logger.debug('📐 Safe area insets обновлены:', insets)
  }

  /**
   * Подписывается на изменения ориентации устройства
   */
  const subscribeToOrientationChanges = (): void => {
    if (!process.client) return

    // Screen Orientation API
    if (window.screen?.orientation) {
      window.screen.orientation.addEventListener('change', () => {
        logger.info('🔄 Ориентация изменилась, обновляем safe area')
        updateSafeArea()
      })
    }

    // Fallback: window resize
    window.addEventListener('resize', debounce(() => {
      logger.debug('🔄 Размер окна изменился, обновляем safe area')
      updateSafeArea()
    }, 300))
  }

  /**
   * Обновляет safe area insets повторным CSS-замером (поворот экрана, ресайз).
   * Нули принимаем как валидный результат — см. initializeNativeSafeArea.
   */
  const updateSafeArea = (): void => {
    if (!Capacitor.isNativePlatform()) return

    try {
      setSafeAreaInsets(getCSSEnvironmentInsets())
    } catch (error) {
      logger.warn('⚠️ Ошибка обновления safe area', error)
    }
  }

  /**
   * Debounce helper
   */
  function debounce<T extends(...args: any[]) => any>(
    func: T,
    wait: number,
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout | null = null
    return (...args: Parameters<T>) => {
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(() => func(...args), wait)
    }
  }

  // Computed properties для удобного использования
  const safeTop = computed(() => safeAreaInsets.value.top)
  const safeBottom = computed(() => safeAreaInsets.value.bottom)
  const safeLeft = computed(() => safeAreaInsets.value.left)
  const safeRight = computed(() => safeAreaInsets.value.right)

  /**
   * Padding для header с учетом safe area
   */
  const headerPadding = computed(() => {
    const basePadding = (width.value > 1024)? 20 : 10 // Базовый padding header
    return `${safeTop.value + basePadding}px`
  })

  /**
   * Padding top для main content с учетом header и safe area
   */
  const contentPaddingTop = computed(() => {
    const headerHeight = 100 // Высота вашего header
    return `${safeTop.value + headerHeight}px`
  })

  /**
   * Padding bottom для контента с учетом safe area
   */
  const contentPaddingBottom = computed(() => {
    return `${safeBottom.value}px`
  })

  /**
   * Inline style для header
   */
  const headerStyle = computed(() => ({
    paddingTop: headerPadding.value,
  }))

  /**
   * Inline style для main content
   */
  const contentStyle = computed(() => ({
    paddingTop: contentPaddingTop.value,
    paddingBottom: contentPaddingBottom.value,
  }))

  return {
    // State
    safeAreaInsets: readonly(safeAreaInsets),
    isInitialized: readonly(isInitialized),
    platform: readonly(platform),

    // Computed values
    safeTop,
    safeBottom,
    safeLeft,
    safeRight,
    headerPadding,
    contentPaddingTop,
    contentPaddingBottom,
    headerStyle,
    contentStyle,

    // Methods
    initialize,
    updateSafeArea,
  }
}
